/**
 * JS 动画演出层（anime.js v4 门面）：编排骨演出一律经这里，⛔ 在组件里散用 animejs
 *
 * 分工铁律（2026-10-07 用户定）：
 *   - CSS transition 继续管 0.1s 级微交互（hover/按压/边框），本模块只管编排骨
 *     （入场序列 / 飞行演出 / 多元素时序），旧效果一律不重写；
 *   - 同一元素同一属性 ⛔ 同时被 CSS transition 与 JS 动画驱动——演出的粒子/临时节点
 *     不带任何 transition，不与既有过渡抢同一属性；
 *   - 手感统一：缓动一律用 easeSettle（对齐 ui.css --ease-settle），⛔ 逐处现编曲线；
 *     时长对齐 ui.css 动效阶梯（DUR 抄值，改档两处同步）。
 *   - prefers-reduced-motion 在本模块入口统一短路，各演出不自行判断。
 *
 * 首个使用点：主页入场编排 menuIntro（2026-10-08 主页场景图门面版重做批）。
 * 此前的「结算句光点爆散 burst」2026-10-07 上线当日被用户撤令删除（VN 卡结算行保持素净）。
 */
import { createTimeline, cubicBezier } from 'animejs';
import { Utils } from '../core/Utils';

/** 入场归缓动，与 ui.css --ease-settle（cubic-bezier(.22,.9,.31,1)）同一条曲线 */
export const easeSettle = cubicBezier(0.22, 0.9, 0.31, 1);

/** 动效阶梯抄值（ui.css --dur-*；CSS 变量 JS 侧读不到默认值，改档两处同步） */
export const DUR = {
    long: 450,    /* --dur-long  .45s 成败脉冲/pop-in/条子生长档 */
    float: 720,   /* --dur-float .72s 一次性演出 */
    loop: 900,    /* --dur-loop  .9s  强演出档 */
} as const;

/** 演出前的统一门槛：reduced-motion / 页面在后台 / 宿主不可见 ⇒ 不播（⛔ 欠账不补播） */
export function canPlayFx(host?: HTMLElement): boolean {
    if (Utils.prefersReducedMotion() || document.hidden) return false;
    return host === undefined || host.getClientRects().length > 0;
}

/**
 * 主页入场编排：标题柔光浮现 → 菜单纸卡升起（2026-10-08 主页撤场景图换纸白后为两段式；
 * 场景图挂回时恢复首段「背景淡入 .72」——旧三段版参数见 git 历史）。
 * 只播一次（模块级旗标）——演出语义是「开场」不是转场，回主页不重播。
 * 元素的隐藏初态由本函数的 timeline 在首帧 set（CSS ⛔ 预写 opacity/transform 隐藏态）：
 * reduced-motion / 后台短路时两元素天然停在终态，页面照常可用。
 * 两元素在 CSS 里均无 transition/animation（同属性双驱动禁令）。
 * 交错 delay 240 属阶梯豁免的 animation-delay 步进族，不入 DUR。
 */
let menuIntroPlayed = false;
export function menuIntro(hero: HTMLElement, card: HTMLElement): void {
    if (menuIntroPlayed) return;
    menuIntroPlayed = true;
    if (!canPlayFx(hero)) return;
    createTimeline()
        .set(hero, { opacity: 0, translateY: '0.8rem' })
        .set(card, { opacity: 0, translateY: '0.8rem' })
        .add(hero, { opacity: 1, translateY: '0rem', duration: DUR.long, ease: easeSettle }, 0)
        .add(card, { opacity: 1, translateY: '0rem', duration: DUR.long, ease: easeSettle }, 240);
}
